import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import { useOficinaExternaOffline } from '../../../hooks/useOficinaExternaOffline';
import type { ItemDaFila } from '../../../infrastructure/offline/oficinaExternaDb';
import { notify } from '../../../shared/feedback/notify';
import BarraDeSincronizacao from '../components/BarraDeSincronizacao';
import CarimboDeDados from '../components/CarimboDeDados';
import SeloDeEnvio from '../components/SeloDeEnvio';
import { dataHora } from '../formatacao';
import { OFICINA_EXTERNA } from '../routes';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../recepcao/pages/Recepcao.css';
import '../../admin-sistema/pages/css/adminSistema.css';
import './OficinaExterna.css';

const ROTULO: Record<ItemDaFila['tipo'], string> = {
  CADASTRO: 'Cadastro de veículo',
  VISITA: 'Visita',
  FOTO: 'Foto da visita',
  VIDEO: 'Vídeo da visita',
  SAIDA: 'Saída da oficina',
};

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que é esta fila',
    conteudo: (
      <>
        <p>
          Tudo o que você registra fica <strong>guardado neste celular</strong> até conseguir subir
          para o sistema. Esta tela mostra o que ainda não subiu e o que o servidor recusou.
        </p>
        <p>
          O envio é automático: assim que houver rede, a fila anda sozinha. Você não precisa fazer
          nada — nem deixar a tela aberta.
        </p>
      </>
    ),
  },
  {
    titulo: 'Os três estados',
    conteudo: (
      <>
        <p>
          <strong>Aguardando rede</strong> — está no aparelho e vai subir sozinho.
        </p>
        <p>
          <strong>Enviado</strong> — já está no sistema. Some desta lista depois de alguns dias.
        </p>
        <p>
          <strong>Recusado</strong> — o servidor não aceitou, e explica o motivo. Este{' '}
          <strong>não</strong> é reenviado sozinho: é preciso decidir o que fazer, porque insistir
          daria o mesmo resultado.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quando a placa já foi cadastrada por outro inspetor',
    conteudo: (
      <>
        <p>
          É o caso mais comum de recusa: dois inspetores cadastraram o mesmo veículo. O cadastro
          seu é recusado, mas <strong>as suas visitas não se perdem</strong> — toque em{' '}
          <strong>Anexar ao cadastro existente</strong> e elas passam a ser registradas no cadastro
          que já está no sistema.
        </p>
        <p>
          <strong>Descartar</strong> apaga o registro deste aparelho, e não há como recuperá-lo. Use
          só quando tiver certeza de que aquilo não deve entrar no sistema.
        </p>
      </>
    ),
  },
  {
    titulo: 'Trocar de celular',
    conteudo: (
      <>
        O que está aguardando envio existe <strong>só neste aparelho</strong>. Antes de trocar de
        celular, desinstalar o aplicativo ou limpar os dados do navegador, conecte-se a uma rede e
        espere a fila esvaziar.
      </>
    ),
  },
];

/**
 * A fila de envio do inspetor (at-114, ADR 0059).
 *
 * 🔴 Existe porque `ENVIADO` só é dito depois do 2xx: o inspetor precisa de um lugar onde ver o
 * que ainda está no aparelho, e o motivo de cada recusa — em vez de descobrir semanas depois que
 * uma visita nunca chegou.
 */
const OficinaExternaFilaPage: React.FC = () => {
  const offline = useOficinaExternaOffline();
  const navigate = useNavigate();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [descartando, setDescartando] = useState<ItemDaFila | null>(null);

  const pendentes = offline.fila.filter((i) => i.estado === 'PENDENTE' || i.estado === 'ENVIANDO');
  const recusados = offline.fila.filter((i) => i.estado === 'RECUSADO');
  const enviados = offline.fila.filter((i) => i.estado === 'ENVIADO');

  const anexar = async (item: ItemDaFila) => {
    const existente = item.erro?.atendimentoExistenteId;
    if (!existente) {
      notify.warning(
        'Não foi possível identificar o cadastro existente. Atualize os dados e tente de novo.',
      );
      return;
    }
    await offline.anexarAoExistente(item.clienteId, existente);
    notify.success('Suas visitas foram anexadas ao cadastro que já existe no sistema.');
  };

  const renderItem = (item: ItemDaFila) => (
    <div
      className={`oe-fila-item ${item.estado === 'RECUSADO' ? 'recusado' : ''}`}
      key={item.clienteId}
    >
      <div className="oe-fila-cabecalho">
        <strong>{ROTULO[item.tipo]}</strong>
        {item.placa && <span>{item.placa}</span>}
        <span className="oe-no-meta">registrado em {dataHora(item.criadoEm)}</span>
      </div>

      {item.estado === 'RECUSADO' && item.erro && (
        <>
          <p className="oe-fila-erro">{item.erro.mensagem}</p>
          <div className="oe-placa-acoes">
            {item.tipo === 'CADASTRO' && item.erro.code === 'PLACA_JA_EM_ATENDIMENTO_EXTERNO' && (
              <button type="button" className="admin-btn admin-btn-primary" onClick={() => void anexar(item)}>
                Anexar ao cadastro existente
              </button>
            )}
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={() => void offline.reenviar(item.clienteId)}
            >
              Tentar de novo
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-danger-outline"
              onClick={() => setDescartando(item)}
            >
              Descartar
            </button>
          </div>
        </>
      )}

      {item.estado !== 'RECUSADO' && item.tentativas > 0 && (
        <p className="oe-no-meta">
          {item.tentativas} tentativa(s) até agora — continua guardado no aparelho.
        </p>
      )}
    </div>
  );

  return (
    <div className="ConteDo recepcao-conte-do">
      {/* Título primeiro, barra depois — o mesmo lugar em toda tela do sistema. */}
      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Registros a enviar
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <SeloDeEnvio
            pendentes={offline.pendentes}
            recusados={offline.recusados}
            online={offline.online}
          />
          <CarimboDeDados baixadoEm={offline.baixadoEm} origem={offline.origem} />
        </div>
      </div>

      <BarraDeSincronizacao
        pendentes={offline.pendentes}
        recusados={offline.recusados}
        online={offline.online}
      />

      <div className="oe-form-acoes" style={{ marginBottom: 12 }}>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={() => navigate(OFICINA_EXTERNA())}>
          Voltar às oficinas
        </button>
        <button
          type="button"
          className="admin-btn admin-btn-primary"
          disabled={!offline.online || offline.pendentes === 0}
          onClick={() => void offline.sincronizar()}
        >
          Enviar agora
        </button>
      </div>

      <h3>Aguardando rede ({pendentes.length})</h3>
      {pendentes.length === 0 ? (
        <p className="oe-vazio">Nada aguardando envio.</p>
      ) : (
        pendentes.map(renderItem)
      )}

      <h3>Recusados ({recusados.length})</h3>
      {recusados.length === 0 ? (
        <p className="oe-vazio">Nenhum registro recusado.</p>
      ) : (
        recusados.map(renderItem)
      )}

      {enviados.length > 0 && (
        <details>
          <summary>Enviados nos últimos dias ({enviados.length})</summary>
          {enviados.map(renderItem)}
        </details>
      )}

      {descartando && (
        <ConfirmModal
          isOpen
          title="Descartar este registro?"
          message={
            `O ${ROTULO[descartando.tipo].toLowerCase()} de ${descartando.placa ?? 'veículo'} será ` +
            'apagado deste aparelho e NÃO entrará no sistema. Não há como recuperá-lo depois.'
          }
          confirmLabel="Descartar"
          onConfirm={() => {
            void offline.descartar(descartando.clienteId);
            setDescartando(null);
          }}
          onCancel={() => setDescartando(null)}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Registros a enviar"
        secoes={helpSections}
      />
    </div>
  );
};

export default OficinaExternaFilaPage;
